import { createFileRoute } from "@tanstack/react-router";
import { CostPageLayout } from "@/components/cost/CostPageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/roof-inspection-cost-phoenix";
const TITLE = "Roof Inspection Cost in Phoenix (Free) | Durafoam Roofing";
const DESCRIPTION =
  "Our roof inspections in Phoenix are free — for homeowners, buyers, sellers, and monsoon prep. Here's what's included and what other inspectors typically charge.";

const faqs = [
  { q: "Are your roof inspections really free?", a: "Yes — completely free for homeowners, buyers, sellers, and monsoon prep across the Phoenix Valley. No obligation, no high-pressure pitch." },
  { q: "What's included in your inspection?", a: "A full walk of the roof, photos of any issues, a written assessment of condition and remaining service life, and a written estimate if any work is recommended. Usually done in 30–45 minutes." },
  { q: "What do other roofers charge for an inspection?", a: "Paid roof inspections in Phoenix typically run $150–$400. Real-estate transaction inspections from a certified inspector run $250–$500. We don't charge because most inspections lead to no work — and that's fine with us." },
  { q: "Do you do real-estate transaction inspections?", a: "Yes. We provide written reports suitable for a real-estate transaction, including photos and an estimate of remaining service life." },
];

export const Route = createFileRoute("/roof-inspection-cost-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Roof Inspection",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <CostPageLayout
      pageSlug="roof-inspection-cost-phoenix"
      eyebrow="Phoenix cost guide"
      h1="Roof Inspection Cost in Phoenix"
      subhead="Ours are free. Here's what most Phoenix inspections cost — and what we include at no charge."
      heroBullets={[
        "Our inspections: free",
        "Written report with photos",
        "30–45 minutes on most homes",
        "No high-pressure follow-up",
      ]}
      intro={
        <>
          <p>
            Most Phoenix roofing companies charge <strong>$150–$400</strong> for a roof
            inspection. Certified third-party inspectors for real-estate transactions usually
            charge <strong>$250–$500</strong>.
          </p>
          <p>
            We don't charge. Free inspections have been part of how we do business since 1989 —
            because most inspections end with "your roof is fine, see you in five years," and
            that's a perfectly good outcome.
          </p>
        </>
      }
      pricingRows={[
        { label: "Durafoam Roofing inspection", range: "FREE", notes: "Includes written report & photos" },
        { label: "Other roofers (typical)", range: "$150–$400", notes: "Often credited if work is done" },
        { label: "Certified RE inspector", range: "$250–$500", notes: "Real-estate transactions" },
        { label: "Insurance / storm inspection", range: "$150–$300", notes: "Hail / wind documentation" },
        { label: "Drone-only inspection", range: "$100–$250", notes: "Limited diagnostic value" },
      ]}
      factors={[
        { title: "What's actually included", body: "A real inspection is walked, not just droned. We get on the roof, lift tiles where needed, and photograph what we find." },
        { title: "Written report", body: "You should leave with photos, a condition summary, and an estimate of remaining service life — in writing. Verbal isn't enough." },
        { title: "Diagnostic depth", body: "Active leaks need source tracing. A 'looks OK from up here' inspection doesn't help when water shows up inside." },
        { title: "Transparency", body: "Look for inspectors who'll tell you 'no work needed.' If every inspection ends in a quote, that's a sales call, not an inspection." },
      ]}
      faqs={faqs}
      ctaTitle="Schedule your free roof inspection"
      ctaBody="Buyer, seller, homeowner, or monsoon prep — call or book online and we'll be out within a few business days."
    />
  );
}
